<template>
  <FormContainer>
    <template #title>Coordonnées du salarié(e)</template>
    <template #content>
      <div class="form__bloc">
        <WrapperInput
          id="field_63"
          v-model="voieModel"
          :label="LABEL_NUMERO_VOIE"
          class="col-12 col-md-18 col-sm-36"
          :placeholder="PLACEHOLDER_ADR_VOIE"
          :required="meta.salarie?.adresse?.voie?.$status === PropertyStatus.Required"
          :alertLabel="meta.salarie?.adresse?.voie?.$messageErreurDirty!"
          :maxlength="38"
          deletable
          :readonly
        />
        <WrapperInput
          id="field_62"
          v-model="complementAdresseModel"
          label="Complément d'adresse"
          class="col-12 col-md-18 col-sm-36"
          :placeholder="PLACEHOLDER_ADR_COMPLEMENT"
          :required="meta.salarie?.adresse?.complement?.$status === PropertyStatus.Required"
          :alertLabel="meta.salarie?.adresse?.complement?.$messageErreurDirty!"
          :maxlength="38"
          deletable
          :readonly
        />
      </div>
      <div class="form__bloc">
        <WrapperAutocomplete
          id="field_67"
          key-id="codeInsee"
          key-label="intitule"
          :search="searchCommune"
          v-model="codePostalModel"
          :get-item-label="getCpCommuneLabel"
          label="Code postal"
          :search-min-length="3"
          :placeholder="PLACEHOLDER_ADR_CP"
          :readonly
          deletable
          class="col-12 col-md-18 col-sm-36"
          :strict="false"
          :required="meta.salarie?.adresse?.codePostal?.$status === PropertyStatus.Required"
          :alertLabel="meta.salarie?.adresse?.codePostal?.$messageErreurDirty!"
          :maxlength="10"
        />
        <WrapperAutocomplete
          id="field_66"
          key-id="codeInsee"
          key-label="intitule"
          :search="searchCommune"
          v-model="communeModel"
          :get-item-label="getCpCommuneLabel"
          label="Commune"
          :search-min-length="3"
          :placeholder="PLACEHOLDER_ADR_VILLE"
          :readonly
          deletable
          class="col-12 col-md-18 col-sm-36"
          :strict="false"
          :required="meta.salarie?.adresse?.commune?.$status === PropertyStatus.Required"
          :alertLabel="meta.salarie?.adresse?.commune?.$messageErreurDirty!"
          :maxlength="50"
        />
      </div>
      <div class="form__bloc">
        <WrapperInputTelephone
          id="field_68"
          v-model="telephoneModel"
          label="Téléphone"
          :required="meta.salarie?.telephone?.$status === PropertyStatus.Required"
          :alertLabel="meta.salarie?.telephone?.$messageErreurDirty!"
          class="col-12 col-md-18 col-sm-36"
          :placeholder="PLACEHOLDER_TEL"
          :maxlength="10"
          deletable
          :readonly
        />
        <WrapperInput
          id="field_65"
          v-model="emailModel"
          label="Email"
          :required="meta.salarie?.email?.$status === PropertyStatus.Required"
          :alertLabel="meta.salarie?.email?.$messageErreurDirty!"
          hint="64 caractères maximum"
          class="col-12 col-md-18 col-sm-36"
          :placeholder="PLACEHOLDER_EMAIL"
          :maxlength="64"
          deletable
          :readonly
        />
      </div>
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import { storeToRefs } from 'pinia'
  import {
    FormContainer,
    PLACEHOLDER_ADR_VOIE,
    PLACEHOLDER_ADR_COMPLEMENT,
    PLACEHOLDER_ADR_CP,
    PLACEHOLDER_ADR_VILLE,
    PLACEHOLDER_TEL,
    PLACEHOLDER_EMAIL,
    LABEL_NUMERO_VOIE,
  } from '@/features/interface/demande/shared'
  import { useDemandeCPROStore } from '@/features/interface/demande/cpro/useDemandeCPROStore'
  import { PropertyStatus } from '@/features/domaine/property-status/PropertyStatus'
  import { CPRO_ACTIONS } from '@/features/interface/demande/cpro/useDemandeCPROActions'
  import { inject } from 'vue'
  import { useCodePostalCommune } from '@/features/interface/composables/useCodePostalCommune'
  import type { AdresseDTO } from '@/features/api/extranet/dto/shared'

  const store = useDemandeCPROStore()
  const { setupFieldWithKey, setupCustomUpdater } = store
  const { meta, readonly, model } = storeToRefs(store)
  const { searchCommune } = inject(CPRO_ACTIONS)!

  const { codePostalModel, communeModel, getCpCommuneLabel } = useCodePostalCommune({
    getCodePostal: () => model?.value?.salarie?.adresse?.codePostal,
    getCommune: () => model?.value?.salarie?.adresse?.commune,
    updateValues: setupCustomUpdater((patch, value) => {
      const salarie = patch.salarie!
      if (!salarie.adresse) {
        salarie.adresse = {} as AdresseDTO
      }
      salarie.adresse.codePostal = value.code ?? salarie.adresse.codePostal
      salarie.adresse.commune = value.commune ?? salarie.adresse.commune
    }),
  })

  const voieModel = setupFieldWithKey<string>('salarie.adresse.voie', true)
  const complementAdresseModel = setupFieldWithKey<string>('salarie.adresse.complement', true)
  const telephoneModel = setupFieldWithKey<string>('salarie.telephone', true)
  const emailModel = setupFieldWithKey<string>('salarie.email', true)
</script>
